import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';

/**
 * Business-role gate — for routes gated to one of the 7 CafeteriaOS
 * predefined roles (`business_role`) rather than the platform authz role
 * `RequireRole` checks. The platform role `admin` (== the "Operator Admin"
 * business role, per `backend/src/middleware/auth.js`'s `requireBusinessRole`)
 * always passes, mirroring the backend guard's `allowPlatformAdmin` default —
 * pass `allowPlatformAdmin={false}` for a route Operator Admin must NOT reach
 * even though they're a platform admin (there is currently no such route;
 * CAF-03's "view" gate wants Operator Admin included).
 *
 *   <Route element={<ProtectedRoute />}>
 *     <Route element={<RequireBusinessRole roles={['Finance Admin']} />}>
 *       <Route path="/client-companies-contracts-management" element={<ClientCompaniesPage />} />
 *     </Route>
 *   </Route>
 *
 * A signed-in user whose business role doesn't match is blocked (redirected
 * to the themed 403 page), never merely hidden.
 */
export default function RequireBusinessRole({ roles, allowPlatformAdmin = true }) {
  const { user } = useAuth();
  const allowed = Array.isArray(roles) ? roles : [roles];
  const isPlatformAdmin = allowPlatformAdmin && user?.role === 'admin';

  if (!user || (!isPlatformAdmin && !allowed.includes(user.business_role))) {
    return <Navigate to="/403" replace />;
  }

  return <Outlet />;
}
